// What the GUEST sees around the booking questions at checkout, the same on
// the widget's cart checkout and on the pay page (/pay/<token>):
//
//   1. "I'm riding — use my details for Rider 1": the booker's first name,
//      last name, mobile and email copied into the first rider's questions
//      whose KEYS are the preset's (first_name, surname, name / full_name,
//      mobile, email), kept in step while the box is ticked, and taken back
//      out when it is unticked. Never into another rider, never into a
//      sensitive or conditional question, and never by a question's label.
//      The page offers the box only while Rider 1 has nothing saved on the
//      server (booking-questions-form.ts riderOne), and reads the forms again
//      before a save that carries the tick: it does not turn a saved rider
//      into someone else. An explicit Yes can also mark a NEW rider as the
//      booker, while their configured name still holds the tracked copy.
//   2. The privacy wording next to the sensitive questions ("Only Demo's
//      managers see these answers."), once per run of sensitive questions.
//      A question the service no longer asks (the server marks it retired
//      below manager, 20260927150010) is never called medical or guardian:
//      an optional one is not asked at all (checkoutQuestions), and a
//      required one reads "Questions no longer asked: managers only" to staff.
//   3. Kept private answers: the guest readers never return a stored
//      sensitive answer to a guest (sensitive_withheld), so after a reload
//      the medical and guardian boxes are empty although the server holds
//      them. A box the stored row shows is "kept": the guest need not answer
//      it again, and a save that leaves it out keeps the stored answer (the
//      server's guest write mode, matched by permanent participant id).
//      Existing riders keep their ids; identity is never guessed from
//      matching names or contact details.
//
// Pure: no React, no network. Nothing here stores or logs an answer.
// Pinned by e2e/guest-checkout-logic.spec.ts.

import {
  isEmptyAnswer,
  isFieldVisible,
  type AnswerRow,
  type FormProblem,
  type GuestNoun,
  type QuestionField,
} from "./booking-questions";
import { identityValue, isRowMarkerKey } from "./participant-ids";

// ─── 1. Rider 1 from the booker's details ───────────────────────────────────

/** Which of the booker's details a question takes ("full_name": first + last, for a form with one name question). */
export type PrefillSlot = "first_name" | "last_name" | "full_name" | "email" | "phone";
export type PrefillTarget = { key: string; slot: PrefillSlot };

/** The booker: the checkout's contact fields, or the pay link's guest. */
export type BookerContact = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
};

// The preset keys, exactly (the trail-ride preset asks first_name / surname /
// mobile / email; the rider-details preset one "name"). Nothing else is
// matched: not a renamed key, not a format, not a label. A form built without
// these keys simply gets no "use my details" box.
const KEY_SLOTS: readonly [string, PrefillSlot][] = [
  ["first_name", "first_name"],
  ["surname", "last_name"],
  ["mobile", "phone"],
  ["email", "email"],
];
/** One question for the whole name: the key "name" or "full_name". */
const FULL_NAME_KEYS: readonly string[] = ["name", "full_name"];

/** A plain, always-shown text question: the only kind a detail is copied into. */
function copyable(field: QuestionField): boolean {
  return field.type === "text" && field.sensitive !== true && !field.show_when;
}

/** The format a slot's question may carry (a name question carries none). */
function formatFits(slot: PrefillSlot, field: QuestionField): boolean {
  const format = field.format ?? null;
  if (slot === "email") return format === null || format === "email";
  if (slot === "phone") return format === null || format === "phone";
  return format === null;
}

/**
 * The questions of one rider's form that take the booker's details, in form
 * order: the plain, always-shown, non-sensitive text questions whose key is
 * exactly first_name, surname, mobile or email — or, on a form that asks
 * neither first_name nor surname, the one whose key is name or full_name
 * (first and last name together). Keys only: never a label, never a format
 * under another key. Each question and each detail once.
 */
export function riderPrefillTargets(fields: readonly QuestionField[]): PrefillTarget[] {
  const byKey = new Map(fields.map((f) => [f.key, f] as const));
  const targets: PrefillTarget[] = [];
  for (const [key, slot] of KEY_SLOTS) {
    const field = byKey.get(key);
    if (field && copyable(field) && formatFits(slot, field)) targets.push({ key, slot });
  }
  if (!byKey.has("first_name") && !byKey.has("surname")) {
    const key = FULL_NAME_KEYS.find((k) => {
      const field = byKey.get(k);
      return !!field && copyable(field) && formatFits("full_name", field);
    });
    if (key) targets.push({ key, slot: "full_name" });
  }
  const order = new Map(fields.map((f, i) => [f.key, i]));
  return targets.sort((a, b) => order.get(a.key)! - order.get(b.key)!);
}

// A stand-in address minted for Paystack when a guest gave none (the bot's
// wa-<digits>@guest.vertu.co.za, the per-booking @pay.vertu.co.za). It is not
// the guest's email, so it is never copied.
const SYNTHETIC_EMAIL = /@(?:guest|pay)\.vertu\.co\.za$/i;

function clean(v: string | null | undefined): string {
  return typeof v === "string" ? v.trim().replace(/\s+/g, " ") : "";
}

/** The booker's value for one slot ("" when they have none). */
export function bookerValue(slot: PrefillSlot, booker: BookerContact): string {
  switch (slot) {
    case "first_name":
      return clean(booker.firstName);
    case "last_name":
      return clean(booker.lastName);
    case "full_name":
      return clean(`${booker.firstName ?? ""} ${booker.lastName ?? ""}`);
    case "email": {
      const email = clean(booker.email);
      return SYNTHETIC_EMAIL.test(email) ? "" : email;
    }
    case "phone":
      return clean(booker.phone);
  }
}

/** A booking's one guest name as first + last: the first word, then the rest. */
export function splitGuestName(name: string | null | undefined): { firstName: string; lastName: string } {
  const words = clean(name).split(" ").filter(Boolean);
  return { firstName: words[0] ?? "", lastName: words.slice(1).join(" ") };
}

/** The pay link's booker: the booking's one guest name, email and phone. */
export function bookerFromGuest(guest: {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
} | null | undefined): BookerContact {
  const { firstName, lastName } = splitGuestName(guest?.name);
  return { firstName, lastName, email: clean(guest?.email), phone: clean(guest?.phone) };
}

/** A copied value, and what the question held before it was copied over. */
export type PrefillCopy = { value: string; previous: unknown };
/** key → the copy. A key the guest changed by hand leaves this map: it is theirs. */
export type PrefillCopies = Record<string, PrefillCopy>;

function textOf(v: unknown): string {
  return typeof v === "string" ? v : typeof v === "number" ? String(v) : "";
}
function blank(v: unknown): boolean {
  return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
}
function put<R extends Record<string, unknown>>(row: R, key: string, value: unknown): void {
  if (blank(value)) delete row[key];
  else (row as Record<string, unknown>)[key] = value;
}

/**
 * The box is ticked: copy each detail the booker has into its question.
 * A detail the booker has not given yet is tracked only when its question is
 * empty (so it follows later); a question already answered is left alone
 * then.
 */
export function startPrefill<R extends Record<string, unknown>>(
  row: R,
  targets: readonly PrefillTarget[],
  booker: BookerContact,
): { row: R; copied: PrefillCopies } {
  const next = { ...row };
  const copied: PrefillCopies = {};
  for (const { key, slot } of targets) {
    const value = bookerValue(slot, booker);
    const current = row[key];
    if (value !== "") {
      put(next, key, value);
      copied[key] = { value, previous: current };
    } else if (blank(current)) {
      copied[key] = { value: "", previous: undefined };
    }
  }
  return { row: next, copied };
}

/**
 * An explicit "I am Rider 1" copy still names the booker. This does not
 * discover identity from matching contacts: callers must already hold the
 * guest's Yes, and every profile-name question must still hold its tracked
 * copy. Hand-editing a name or using an unmarked legacy form cannot create
 * a booker link. Email/phone edits do not change who the rider is.
 */
export function prefilledBooker(
  fields: readonly QuestionField[],
  row: Readonly<Record<string, unknown>>,
  copied: PrefillCopies,
): boolean {
  const names = fields.filter((field) =>
    field.profile === "first_name" || field.profile === "last_name" || field.profile === "full_name",
  );
  const targets = new Map(riderPrefillTargets(fields).map(({ key, slot }) => [key, slot]));
  return names.length > 0 && names.every((field) => {
    const copy = copied[field.key];
    return targets.get(field.key) === field.profile && !!copy &&
      identityValue(copy.value) !== "" && identityValue(row[field.key]) === identityValue(copy.value);
  });
}

/**
 * The booker changed a detail while the box is ticked: it flows into the
 * questions still holding what was copied. A question the guest changed by
 * hand is theirs from then on (it leaves `copied`). Returns the same objects
 * when nothing changed.
 */
export function syncPrefill<R extends Record<string, unknown>>(
  row: R,
  targets: readonly PrefillTarget[],
  booker: BookerContact,
  copied: PrefillCopies,
): { row: R; copied: PrefillCopies } {
  let nextRow = row;
  let nextCopied = copied;
  for (const { key, slot } of targets) {
    const copy = copied[key];
    if (!copy) continue;
    if (textOf(row[key]) !== copy.value) {
      nextCopied = { ...nextCopied };
      delete nextCopied[key];
      continue;
    }
    const value = bookerValue(slot, booker);
    if (value === copy.value) continue;
    if (nextRow === row) nextRow = { ...row };
    put(nextRow, key, value);
    nextCopied = { ...nextCopied, [key]: { ...copy, value } };
  }
  return { row: nextRow, copied: nextCopied };
}

/**
 * The box is unticked: every question still holding what was copied goes
 * back to what it held before (empty, as a rule). Questions the guest
 * changed by hand keep their answers.
 */
export function stopPrefill<R extends Record<string, unknown>>(row: R, copied: PrefillCopies): R {
  let next = row;
  for (const [key, copy] of Object.entries(copied)) {
    if (textOf(row[key]) !== copy.value) continue;
    if (next === row) next = { ...row };
    put(next, key, copy.previous);
  }
  return next;
}

/**
 * Has the server saved anything for this rider? A stored row that holds any
 * answer; or, where the reader withholds the list's private answers
 * (`privateWithheld`), any stored row at all: its private answers are not
 * returned, so a row that looks empty may still hold them. "I'm riding — use
 * my details" is offered only while this is false for Rider 1, so the box
 * fills in a rider nobody has saved yet, not a saved rider (the page checks
 * it again on fresh forms before a save that carries the tick). A plain rule
 * on what is stored: it never compares answers.
 */
export function riderSaved(storedRow: unknown, privateWithheld: boolean): boolean {
  if (!storedRow || typeof storedRow !== "object" || Array.isArray(storedRow)) return false;
  if (privateWithheld) return true;
  // A rider's id is not an answer: a placeholder {_pid} holds nothing yet.
  return Object.entries(storedRow as Record<string, unknown>).some(([k, v]) => !isRowMarkerKey(k) && !blank(v));
}

/** "I'm riding — use my details for Rider 1" (in the service's guest noun). */
export function prefillLabel(noun: GuestNoun, number = 1): string {
  const one = noun.one.trim() || "guest";
  const title = one.charAt(0).toUpperCase() + one.slice(1);
  const who = one.toLowerCase() === "rider" ? "I'm riding" : `I'm one of the ${noun.other.trim() || "guests"}`;
  return `${who} — use my details for ${title} ${number}`;
}

// ─── 2. The privacy wording ─────────────────────────────────────────────────

/** "Demo's managers", "Fijnland Outrides' managers"; "the venue's managers" with no name. */
export function venueManagers(venueName: string | null | undefined): string {
  const name = clean(venueName);
  if (!name) return "the venue's managers";
  return /s$/i.test(name) ? `${name}' managers` : `${name}'s managers`;
}

/** Shown to the GUEST next to the sensitive questions (never the staff line). */
export function sensitiveNote(venueName: string | null | undefined): string {
  return `Only ${venueManagers(venueName)} see these answers.`;
}

/**
 * Shown on an empty private question the server may hold an answer to. The
 * reader never says whether an OPTIONAL one was answered, so the words stay
 * true either way.
 */
export function savedSensitiveNote(type: QuestionField["type"]): string {
  const how =
    type === "text" || type === "longtext" || type === "number"
      ? "type to change"
      : type === "checkbox"
        ? "tick to change"
        : "choose to change";
  return `Kept private — ${how}.`;
}

/** An optional private answer the guest chose to clear (sent as an explicit null). */
export const CLEARED_SENSITIVE_NOTE = "Cleared — the kept answer is removed when you save.";

/** The staff line: a signed-in member below manager sees these questions but cannot answer them. */
export const STAFF_SENSITIVE_NOTE = "Medical and guardian details: managers only";

/**
 * The staff line over questions the service no longer asks (retired): their
 * answers are withheld below manager only because nothing says whether they
 * were private, so they are never called medical or guardian.
 */
export const STAFF_RETIRED_NOTE = "Questions no longer asked: managers only";

/**
 * The questions the checkout and the pay page put to a caller below manager
 * (a guest, a link holder, staff). The readers keep, marked retired (and
 * sensitive: the answer is withheld), each question the service no longer
 * asks that the booking's own copy still has. An OPTIONAL one is left off:
 * nothing needs answering, and a save that leaves it out keeps the stored
 * answer. A REQUIRED one stays, because the booking's copy still requires it
 * before payment. Never for a manager: a manager's save is stored as sent,
 * so a question left off would erase its answer.
 */
export function checkoutQuestions(fields: readonly QuestionField[]): QuestionField[] {
  return fields.filter((f) => !(f.retired === true && f.required !== true));
}

/**
 * Where the privacy line goes: for each sensitive question shown, the key of
 * the first question of its run (consecutive shown sensitive questions; a
 * hidden question does not break a run). The line is printed once, above the
 * run's first question, and every question of the run points at it.
 * byKind: a question no longer asked (retired) and a private one never share
 * a run, so each gets its own line (privacyNote); the staff view needs it.
 */
export function sensitiveRuns(
  fields: readonly QuestionField[],
  shown: (field: QuestionField) => boolean,
  opts: { byKind?: boolean } = {},
): Map<string, string> {
  const runs = new Map<string, string>();
  let start: string | null = null;
  let retired = false;
  for (const field of fields) {
    if (!shown(field)) continue;
    if (field.sensitive === true) {
      if (opts.byKind && start !== null && (field.retired === true) !== retired) start = null;
      if (start === null) retired = field.retired === true;
      start ??= field.key;
      runs.set(field.key, start);
    } else {
      start = null;
    }
  }
  return runs;
}

/**
 * The privacy line above a run, from the run's first question. A guest reads
 * "Only Demo's managers see these answers." (true of both kinds); staff read
 * the medical line, or the "no longer asked" line for a retired run.
 */
export function privacyNote(
  first: Pick<QuestionField, "retired">,
  staff: boolean,
  venueName: string | null | undefined,
): string {
  if (!staff) return sensitiveNote(venueName);
  return first.retired === true ? STAFF_RETIRED_NOTE : STAFF_SENSITIVE_NOTE;
}

// ─── 3. Saved sensitive answers ─────────────────────────────────────────────

/**
 * The sensitive questions of one STORED row that the server may hold an
 * answer for without returning it: every sensitive question the stored row
 * shows, except one the row itself says is empty (a local copy after a save
 * that cleared it). Call it only for a guest's withheld form, and only for
 * a row storedRowsCounted counts (the server carries a withheld answer only
 * by position). A question that hangs on a withheld answer is not shown by
 * the stored row, so it is not "saved": answer the controller again and it
 * is asked like any other.
 */
export function savedSensitiveKeys(
  fields: readonly QuestionField[],
  storedRow: AnswerRow | null | undefined,
): Set<string> {
  const keys = new Set<string>();
  const row = storedRow ?? {};
  for (const field of fields) {
    if (field.sensitive !== true || !isFieldVisible(field, row)) continue;
    if (Object.prototype.hasOwnProperty.call(row, field.key) && isEmptyAnswer(field, row[field.key])) continue;
    keys.add(field.key);
  }
  return keys;
}

/**
 * Which STORED rows of a guest's withheld form may be counted as holding
 * their withheld private answers (savedSensitiveKeys), one flag per row;
 * [] when none may. Only while the stored rows line up with the party:
 *   * the server reads the form complete: every row;
 *   * riders saved one at a time (save_booking_participant_row), some still
 *     not in: every rider the reader returned with an answer (riderSaved),
 *     never one with none ({}, a {_pid} placeholder, only blanks). The
 *     server stores a rider only when the row is valid on its own
 *     (invariant I1), so such a row holds every private answer its
 *     questions show, and a reload keeps them kept;
 *   * every row answered and the server still calls the form incomplete, or
 *     nobody in yet: none, so the guest answers and the server judges anew.
 * A rider the reader returns with no answer may still hold private answers
 * on a list that asks nothing else of them; it is not counted (the guest is
 * asked again, never told an answer is kept that is not).
 */
export function storedRowsCounted(
  participants: readonly unknown[],
  partySize: number,
  complete: boolean | null,
): boolean[] {
  if (participants.length !== partySize) return [];
  if (complete === true) return participants.map(() => true);
  const answered = participants.map((row) => riderSaved(row, false));
  return answered.some(Boolean) && !answered.every(Boolean) ? answered : [];
}

/**
 * The problems left once saved answers count: an EMPTY answer to a saved
 * question is not missing (the save leaves it out and the server keeps the
 * stored one). Anything the guest typed is judged as usual.
 */
export function withoutSavedProblems(
  problems: readonly FormProblem[],
  saved: readonly (ReadonlySet<string> | undefined)[],
): FormProblem[] {
  return problems.filter((p) => !(p.code === "required" && p.row >= 0 && saved[p.row]?.has(p.key)));
}
